import React, { useState } from 'react';
import { firebaseAppointmentService, ProviderAvailability } from '../services/firebase/FirebaseAppointmentService';

interface MultiMonthCalendarProps {
  providerId: string;
  onDateSelect: (date: Date) => void;
  selectedDate?: Date;
  availabilityData?: ProviderAvailability[];
}

const MultiMonthCalendar: React.FC<MultiMonthCalendarProps> = ({
  providerId,
  onDateSelect,
  selectedDate,
  availabilityData
}) => {
  const [startMonth, setStartMonth] = useState(new Date());
  const [refreshKey, setRefreshKey] = useState(0);

  // Force refresh of availability data periodically or on focus
  React.useEffect(() => {
    const handleFocus = () => {
      setRefreshKey(prev => prev + 1);
    };

    window.addEventListener('focus', handleFocus);

    const intervalId = setInterval(() => {
      setRefreshKey(prev => prev + 1);
    }, 30000);

    return () => {
      window.removeEventListener('focus', handleFocus);
      clearInterval(intervalId);
    };
  }, []);

  // Generate 3 months starting from startMonth
  const getMonths = (): Date[] => {
    const months: Date[] = [];
    for (let i = 0; i < 3; i++) {
      const month = new Date(startMonth);
      month.setMonth(startMonth.getMonth() + i);
      months.push(month);
    }
    return months;
  };

  const getDaysInMonth = (date: Date): Date[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days: Date[] = [];

    // Add days from previous month to fill the first week
    const firstDayOfWeek = firstDay.getDay();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const prevDate = new Date(year, month, -i);
      days.push(prevDate);
    }

    // Add days of current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }

    // Add days from next month to complete the grid
    const remainingDays = 42 - days.length; // 6 weeks * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push(new Date(year, month + 1, i));
    }

    return days;
  };

  // Fallback: load availability from Firestore when no availabilityData prop
  const [fallbackAvailability, setFallbackAvailability] = React.useState<ProviderAvailability[]>([]);
  React.useEffect(() => {
    if (!availabilityData || availabilityData.length === 0) {
      firebaseAppointmentService.getProviderAvailability(providerId)
        .then(avail => setFallbackAvailability(avail))
        .catch(err => console.error('MultiMonthCalendar: failed to load availability', err));
    }
  }, [providerId, availabilityData, refreshKey]);

  // Check if date has any available slots
  const hasAvailability = React.useCallback((date: Date): boolean => {
    const data = (availabilityData && availabilityData.length > 0) ? availabilityData : fallbackAvailability;
    const dayOfWeek = date.getDay();
    const dayAvail = data.find(a => a.dayOfWeek === dayOfWeek && a.isActive);
    return !!dayAvail;
  }, [providerId, availabilityData, fallbackAvailability, refreshKey]);

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isPastDate = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < today;
  };

  const isSelectedDate = (date: Date): boolean => {
    if (!selectedDate) return false;
    return date.toDateString() === selectedDate.toDateString();
  };

  const isCurrentMonth = (date: Date, monthDate: Date): boolean => {
    return date.getMonth() === monthDate.getMonth();
  };

  const goToPreviousMonths = () => {
    const newDate = new Date(startMonth);
    newDate.setMonth(newDate.getMonth() - 3);
    setStartMonth(newDate);
  };

  const goToNextMonths = () => {
    const newDate = new Date(startMonth);
    newDate.setMonth(newDate.getMonth() + 3);
    setStartMonth(newDate);
  };

  const goToToday = () => {
    setStartMonth(new Date());
  };

  const months = getMonths();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div style={{
      backgroundColor: '#1e293b',
      borderRadius: '8px',
      padding: '20px'
    }}>
      {/* Navigation */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <h3 style={{ margin: 0, color: '#f9fafb', fontSize: '18px' }}>
          Select a Date
        </h3>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={goToPreviousMonths}
            aria-label="Go to previous 3 months"
            style={{
              padding: '6px 12px',
              backgroundColor: '#374151',
              color: '#f9fafb',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px'
            }}
          >
            ← Previous 3 Months
          </button>
          <button
            onClick={goToToday}
            style={{
              padding: '6px 12px',
              backgroundColor: '#6CA6C1',
              color: '#1e293b',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 'bold'
            }}
          >
            Today
          </button>
          <button
            onClick={goToNextMonths}
            aria-label="Go to next 3 months"
            style={{
              padding: '6px 12px',
              backgroundColor: '#374151',
              color: '#f9fafb',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px'
            }}
          >
            Next 3 Months →
          </button>
        </div>
      </div>

      {/* 3-Month Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '20px'
      }}>
        {months.map((month, monthIndex) => {
          const days = getDaysInMonth(month);

          return (
            <div key={monthIndex}>
              {/* Month Title */}
              <h4 style={{
                margin: '0 0 12px 0',
                color: '#f9fafb',
                fontSize: '16px',
                textAlign: 'center'
              }}>
                {month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h4>

              {/* Weekday Headers */}
              <div role="row" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: '4px',
                marginBottom: '4px'
              }}>
                {weekDays.map(day => (
                  <div
                    key={day}
                    role="columnheader"
                    aria-label={day}
                    style={{
                      textAlign: 'center',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      color: '#d1d5db',
                      padding: '4px'
                    }}
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Days Grid */}
              <div
                role="grid"
                aria-label={`${month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} calendar`}
                style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: '4px'
              }}>
                {days.map((date, dayIndex) => {
                  const isAvailable = hasAvailability(date);
                  const isTodayDate = isToday(date);
                  const isPast = isPastDate(date);
                  const isSelected = isSelectedDate(date);
                  const isCurrentMonthDay = isCurrentMonth(date, month);
                  const isClickable = isAvailable && !isPast && isCurrentMonthDay;

                  let bgColor = '#28527A';
                  if (isPast) {
                    bgColor = '#1f2937';
                  } else if (isSelected) {
                    bgColor = '#6CA6C1';
                  } else if (isAvailable && isCurrentMonthDay) {
                    bgColor = '#065f46'; // Green for available
                  } else if (isCurrentMonthDay) {
                    bgColor = '#374151'; // Gray for no availability
                  }

                  const dateLabel = date.toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric'
                  });
                  const ariaLabel = `${dateLabel}${isAvailable && isCurrentMonthDay ? ', available' : ''}${isTodayDate ? ', today' : ''}${isSelected ? ', selected' : ''}${isPast ? ', past date' : ''}`;

                  return (
                    <div
                      key={dayIndex}
                      role={isClickable ? 'button' : undefined}
                      tabIndex={isClickable ? 0 : -1}
                      aria-label={ariaLabel}
                      aria-selected={isSelected || undefined}
                      aria-disabled={!isClickable || undefined}
                      onClick={() => isClickable && onDateSelect(date)}
                      onKeyDown={(e) => {
                        if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          onDateSelect(date);
                        }
                      }}
                      style={{
                        minHeight: '40px',
                        padding: '6px',
                        backgroundColor: bgColor,
                        borderRadius: '4px',
                        cursor: isClickable ? 'pointer' : 'default',
                        opacity: isCurrentMonthDay ? 1 : 0.3,
                        border: isTodayDate ? '2px solid #fbbf24' : isSelected ? '2px solid #1e293b' : 'none',
                        transition: 'all 0.2s',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative'
                      }}
                      onMouseEnter={(e) => {
                        if (isClickable) {
                          e.currentTarget.style.backgroundColor = '#047857';
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = bgColor;
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      <div style={{
                        fontSize: '13px',
                        fontWeight: isTodayDate ? 'bold' : 'normal',
                        color: isSelected ? '#1e293b' : '#f9fafb'
                      }}>
                        {date.getDate()}
                      </div>
                      {isAvailable && isCurrentMonthDay && !isPast && (
                        <div style={{ fontSize: '10px', marginTop: '2px' }}>
                          ✓
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginTop: '16px',
        fontSize: '12px',
        color: '#d1d5db',
        justifyContent: 'center',
        flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '16px', height: '16px', backgroundColor: '#065f46', borderRadius: '3px' }} />
          <span>Available</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '16px', height: '16px', backgroundColor: '#374151', borderRadius: '3px' }} />
          <span>No Availability</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '16px', height: '16px', backgroundColor: '#6CA6C1', borderRadius: '3px' }} />
          <span>Selected</span>
        </div>
      </div>
    </div>
  );
};

export default MultiMonthCalendar;
